import * as z from "zod";

/**
 * Who wrote an internal note. `user` is a person on the merchant's team; `ai` is
 * the reply agent writing its own bookkeeping; `system` is Redo itself.
 */
export const RecoverInternalNoteAuthorType = {
  User: "user",
  Ai: "ai",
  System: "system",
} as const;
export type RecoverInternalNoteAuthorType =
  (typeof RecoverInternalNoteAuthorType)[keyof typeof RecoverInternalNoteAuthorType];

/**
 * A Support V2 internal note shown alongside a Recover conversation. Notes live
 * on the V2 channel thread, so `id` is the CRDB `message` id as a decimal string.
 */
const recoverInternalNoteSchema_ = z.object({
  id: z.string(),
  authorName: z.string(),
  content: z.string(),
  timestamp: z.date(),
});

export interface RecoverInternalNote extends z.infer<
  typeof recoverInternalNoteSchema_
> {}

export const recoverInternalNoteSchema: z.ZodType<RecoverInternalNote> =
  recoverInternalNoteSchema_;

/**
 * Maps a V2 `message_sender_type` onto note authorship. Takes a plain string so
 * this stays free of an effect-api dependency; unknown values read as `system`.
 */
export function toRecoverInternalNoteAuthorType(
  senderType: string,
): RecoverInternalNoteAuthorType {
  switch (senderType) {
    case "merchant":
      return RecoverInternalNoteAuthorType.User;
    case "ai":
    case "auto_reply":
    case "chat_flow":
      return RecoverInternalNoteAuthorType.Ai;
    default:
      return RecoverInternalNoteAuthorType.System;
  }
}

/**
 * Display name for a note's author. Only `user` notes carry a real person, so
 * an unresolvable one must not fall back to something that reads like a name.
 */
export function recoverInternalNoteAuthorName(params: {
  authorType: RecoverInternalNoteAuthorType;
  resolvedName?: string;
}): string {
  switch (params.authorType) {
    case RecoverInternalNoteAuthorType.User:
      return params.resolvedName || "Unknown user";
    case RecoverInternalNoteAuthorType.Ai:
      return "Commerce Agent";
    case RecoverInternalNoteAuthorType.System:
      return "System";
  }
}
